/**
 * queue/liveAdapter.ts
 * Maps the live read models (LiveView line + FloorView resources) into the
 * in-memory store's domain shapes (Entry[] / Resource[]) so the Host Console and
 * its modals render identically whether the data is live or seeded. The store
 * swaps its slices for these in live mode; the screen/modals never branch.
 *
 * The live line is thinner than the demo Entry (no per-entry event ledger, no
 * client joinAt/consent) — we fill what the console actually reads and derive
 * joinAt from waitedSeconds so FIFO ordering + wait display still work.
 */

import type { Entry, Resource, EntryState, Channel } from './types';
import type { LiveView, FloorView } from '../api/queueDtos';

/** Live line item → store Entry (only the fields the console renders). */
export function liveLineToEntries(view: LiveView, now: number): Entry[] {
  return view.line.map((l) => ({
    id: l.entryId,
    token: l.token,
    // Guests self-join with phone only (no name), so the server name is null.
    // Fall back to "Guest" — a blank dash read as a rendering glitch in the
    // console, and "Guest" gives a real label + a "G" avatar. The QR channel
    // badge already distinguishes it from a named walk-in.
    displayName: l.name ?? 'Guest',
    phone: l.maskedPhone ?? '',
    party: l.partySize ?? 1,
    channel: l.channel as Channel,
    state: l.state as EntryState,
    // Derive a stable joinAt from the server's waited time (FIFO key + wait UI).
    joinAt: now - l.waitedSeconds * 1000,
    allocatedResource: null, // set from the floor overlay below
    consent: true,
    verified: true,
    events: [],
  }));
}

/** Floor resources → store Resource[]; also stamps allocatedResource on entries. */
export function floorToResources(floor: FloorView, entries: Entry[]): Resource[] {
  // Stamp each occupied/held table's ref onto its holder entry so the console's
  // "Seat → T4 held" / board "table" rendering works off allocatedResource.
  for (const r of floor.resources) {
    const holder = r.occupant?.entryId ?? r.heldFor?.entryId ?? null;
    if (holder) {
      const e = entries.find((x) => x.id === holder);
      if (e) e.allocatedResource = r.ref;
    }
  }
  return floor.resources.map((r) => ({
    id: r.ref,
    label: r.label,
    cap: r.capacity,
    zone: r.zone ?? '',
    shared: r.shared === true,
    status: r.status as Resource['status'],
    occupant: r.occupant?.entryId ?? null,
    heldFor: r.heldFor?.entryId ?? null,
    seatsTaken: r.seatsTaken,
    partyCount: r.partyCount,
  }));
}
